import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Download, FileText, Loader2, Sparkles } from 'lucide-react';
import { Button, Card, Tag } from '@/components/ui';
import {
  exportSessionUrl,
  generateSessionReview,
  listSessions,
  type SessionListItem,
} from '@/lib/api-client';
import { ApiError } from '@/lib/api';
import { useMatters } from '@/contexts/MatterContext';

export function Sessions() {
  const { matters } = useMatters();
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matterFilter, setMatterFilter] = useState<string>('all');
  // Courtroom-ready sprint: practice sessions stay out of the real history by
  // default; one toggle shows them, clearly tagged.
  const [showRehearsals, setShowRehearsals] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const matterId = matterFilter === 'all' ? undefined : matterFilter;
    listSessions(matterId)
      .then((rows) => {
        if (!cancelled) {
          setSessions(rows);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        if (err instanceof ApiError && err.status === 401) {
          setSessions([]);
        } else {
          setError(err instanceof Error ? err.message : 'Could not load sessions');
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [matterFilter]);

  const rehearsalCount = sessions.filter((s) => s.isRehearsal).length;
  const visibleSessions = showRehearsals ? sessions : sessions.filter((s) => !s.isRehearsal);

  return (
    <main className="min-h-screen bg-stone-950 px-6 py-10">
      <div className="mx-auto max-w-4xl space-y-6">
        <Link
          to="/hearing"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to hearing
        </Link>

        <header className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500">Sessions</p>
          <h1 className="font-display text-3xl italic text-amber-500">Hearing sessions</h1>
          <p className="text-sm text-stone-400">
            One session per hearing or work block. Open any to review the full transcript and Claude
            responses, or export to PDF or Word for filing.
          </p>
        </header>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Matter:</span>
          <FilterButton
            active={matterFilter === 'all'}
            onClick={() => setMatterFilter('all')}
            label="All"
          />
          {matters.map((m) => (
            <FilterButton
              key={m.id}
              active={matterFilter === m.id}
              onClick={() => setMatterFilter(m.id)}
              label={m.name}
            />
          ))}
          {rehearsalCount > 0 ? (
            <button
              type="button"
              onClick={() => setShowRehearsals((v) => !v)}
              className={
                showRehearsals
                  ? 'ml-auto rounded border border-dashed border-amber-500/70 bg-amber-950/30 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-amber-400'
                  : 'ml-auto rounded border border-stone-800 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-stone-500 hover:text-stone-300'
              }
            >
              {showRehearsals ? `Hiding nothing (${rehearsalCount} rehearsal)` : `Show rehearsals (${rehearsalCount})`}
            </button>
          ) : null}
        </div>

        {error ? (
          <Card variant="default" padding="md" className="border-red-900/40 bg-red-950/10">
            <p className="text-sm text-red-300">Could not load sessions: {error}</p>
          </Card>
        ) : null}

        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Card key={i} variant="panel" padding="md" className="space-y-2">
                <div className="h-4 w-1/2 animate-pulse rounded bg-stone-800" />
                <div className="h-3 w-1/3 animate-pulse rounded bg-stone-800/70" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-stone-800/50" />
              </Card>
            ))}
          </div>
        ) : visibleSessions.length === 0 ? (
          <Card variant="panel" padding="lg" className="text-center">
            <FileText className="mx-auto mb-3 h-7 w-7 text-amber-500" />
            <p className="font-display text-xl italic text-stone-100">No sessions yet</p>
            <p className="mt-1 text-sm text-stone-400">
              Start listening on the hearing screen to create a session.
            </p>
          </Card>
        ) : (
          <div className="space-y-2">
            {visibleSessions.map((s) => (
              <SessionRow key={s.id} session={s} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
}

function FilterButton({ active, onClick, label }: FilterButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'rounded-full border border-amber-900/40 bg-amber-950/20 px-3 py-1 text-xs text-amber-400'
          : 'rounded-full border border-stone-800 bg-stone-900 px-3 py-1 text-xs text-stone-400 hover:border-stone-700 hover:text-stone-200'
      }
    >
      {label}
    </button>
  );
}

function SessionRow({ session }: { session: SessionListItem }) {
  const started = new Date(session.startedAt);
  const ended = session.endedAt ? new Date(session.endedAt) : null;
  const minutes = ended ? Math.round((ended.getTime() - started.getTime()) / 60_000) : null;

  const [review, setReview] = useState<string | null>(null);
  const [reviewMsg, setReviewMsg] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);

  async function runReview(): Promise<void> {
    setReviewing(true);
    setReviewMsg(null);
    try {
      const r = await generateSessionReview(session.id);
      if (r.skipped) {
        setReviewMsg(`No review yet: ${r.reason ?? 'not enough to review'}.`);
        setReview(null);
      } else {
        setReview(r.review);
      }
    } catch (err) {
      setReviewMsg(err instanceof ApiError ? err.message : 'Could not generate the review.');
    } finally {
      setReviewing(false);
    }
  }

  return (
    <Card variant="panel" padding="md" className="space-y-3">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <p className="font-display text-base italic text-stone-100">{session.matter.name}</p>
          <p className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-stone-500">
            {[session.matter.reference, session.matter.court].filter(Boolean).join(' · ') ||
              'No reference'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-stone-400">
            <Clock className="h-3.5 w-3.5 text-stone-500" />
            <span>{started.toLocaleString()}</span>
            {minutes !== null ? <Tag tone="muted">{minutes} min</Tag> : <Tag tone="accent">Open</Tag>}
            <Tag tone="neutral">{session.responseCount} responses</Tag>
            {session.isRehearsal ? (
              <span className="rounded border border-dashed border-amber-500/70 bg-amber-950/30 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.18em] text-amber-400">
                Rehearsal
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={
              reviewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />
            }
            onClick={() => void runReview()}
            disabled={reviewing}
          >
            Review
          </Button>
          <a href={exportSessionUrl(session.id, 'pdf')} target="_blank" rel="noreferrer">
            <Button variant="ghost" size="sm" leftIcon={<Download className="h-3.5 w-3.5" />}>
              PDF
            </Button>
          </a>
          <a href={exportSessionUrl(session.id, 'docx')} target="_blank" rel="noreferrer">
            <Button variant="ghost" size="sm" leftIcon={<Download className="h-3.5 w-3.5" />}>
              DOCX
            </Button>
          </a>
        </div>
      </div>
      {reviewMsg ? <p className="text-xs text-stone-500">{reviewMsg}</p> : null}
      {review ? (
        <article className="whitespace-pre-wrap rounded-md border border-stone-800 bg-stone-950/60 p-3 text-sm leading-relaxed text-stone-200">
          {review}
        </article>
      ) : null}
    </Card>
  );
}
